import { createAsyncThunk } from "@reduxjs/toolkit";
import InvoicesApiService from "../../infrastructure/api/invoices";
import PaymentsApiService from "../../infrastructure/api/payments";
import SettlementsApiService from "../../infrastructure/api/settlements";
import WalletsApiService from "../../infrastructure/api/wallets";
import { setMessage } from "../slices/message";

// Wallet Actions
export const fetchWallet = createAsyncThunk(
  "financial/fetchWallet",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await WalletsApiService.getMyWallet();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت اطلاعات کیف پول";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchWalletBalance = createAsyncThunk(
  "financial/fetchBalance",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await WalletsApiService.getBalance();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت موجودی کیف پول";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchWalletTransactions = createAsyncThunk(
  "financial/fetchTransactions",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await WalletsApiService.getTransactions(params);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت تراکنش‌ها";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const depositToWallet = createAsyncThunk(
  "financial/deposit",
  async ({ amount, description }, { dispatch, rejectWithValue }) => {
    try {
      const response = await WalletsApiService.deposit(amount, description);
      dispatch(setMessage("واریز با موفقیت انجام شد"));
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "خطا در واریز وجه";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const withdrawFromWallet = createAsyncThunk(
  "financial/withdraw",
  async ({ amount, description }, { dispatch, rejectWithValue }) => {
    try {
      const response = await WalletsApiService.withdraw(amount, description);
      dispatch(setMessage("برداشت با موفقیت انجام شد"));
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "خطا در برداشت وجه";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Settlement Actions
export const fetchSettlements = createAsyncThunk(
  "financial/fetchSettlements",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await SettlementsApiService.getMySettlements();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت تسویه‌حساب‌ها";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const requestSettlement = createAsyncThunk(
  "financial/requestSettlement",
  async (settlementData, { dispatch, rejectWithValue }) => {
    try {
      const response = await SettlementsApiService.requestSettlement(
        settlementData
      );
      dispatch(setMessage("درخواست تسویه با موفقیت ثبت شد"));
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در ثبت درخواست تسویه";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Invoice Actions
export const fetchInvoices = createAsyncThunk(
  "financial/fetchInvoices",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await InvoicesApiService.getMyInvoices();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت فاکتورها";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchInvoiceDetails = createAsyncThunk(
  "financial/fetchInvoiceDetails",
  async (invoiceId, { dispatch, rejectWithValue }) => {
    try {
      const response = await InvoicesApiService.getInvoiceDetails(invoiceId);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت جزئیات فاکتور";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const createInvoiceFromOrder = createAsyncThunk(
  "financial/createInvoiceFromOrder",
  async (orderId, { dispatch, rejectWithValue }) => {
    try {
      const response = await InvoicesApiService.createInvoiceFromOrder(orderId);
      dispatch(setMessage("فاکتور با موفقیت ایجاد شد"));
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "خطا در ایجاد فاکتور";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Payment Actions
export const fetchMyPayments = createAsyncThunk(
  "financial/fetchPayments",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await PaymentsApiService.getMyPayments();
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "خطا در دریافت پرداخت‌ها";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const initiatePayment = createAsyncThunk(
  "financial/initiatePayment",
  async (paymentData, { dispatch, rejectWithValue }) => {
    try {
      const response = await PaymentsApiService.initiatePayment(paymentData);
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "خطا در شروع پرداخت";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);

export const verifyPayment = createAsyncThunk(
  "financial/verifyPayment",
  async ({ authority, status }, { dispatch, rejectWithValue }) => {
    try {
      const response = await PaymentsApiService.verifyPayment(
        authority,
        status
      );
      if (response.data?.isSuccess) {
        dispatch(setMessage("پرداخت با موفقیت انجام شد"));
      } else {
        dispatch(setMessage("پرداخت ناموفق بود"));
      }
      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "خطا در تأیید پرداخت";
      dispatch(setMessage(message));
      return rejectWithValue(message);
    }
  }
);
